import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";

const LINKS = [
    { label: "Subjects", hash: "#subjects" },
    { label: "Our Mission", hash: "#mission" },
    { label: "Teachers", hash: "#teachers" },
    { label: "Pricing", hash: "#pricing" },
];

const scrollToHash = (hash) => {
    const el = document.querySelector(hash);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
    else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const goTo = (hash) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToHash(hash), 150);
        } else {
            scrollToHash(hash);
        }
    };

    const linkCls =
        "relative text-sm font-medium text-ink/80 transition-colors duration-300 hover:text-saffron after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:rounded-full after:bg-saffron after:transition-all after:duration-300 hover:after:w-full";

    return (
        <header className="sticky top-0 z-50" data-testid="site-navbar">
            <div className="h-1 w-full bg-gradient-to-r from-saffron via-saffron-soft to-teal" aria-hidden />
            <div className="border-b border-teal/20 bg-white/85 shadow-[0_1px_12px_rgba(13,59,38,0.06)] backdrop-blur-xl">
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
                <Link to="/" data-testid="nav-home-link" onClick={() => setOpen(false)}>
                    <Logo />
                </Link>
                <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links-desktop">
                    {LINKS.map((l) => (
                        <button
                            key={l.hash}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            onClick={() => goTo(l.hash)}
                            className={linkCls}
                        >
                            {l.label}
                        </button>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-link-contact"
                        className={linkCls}
                    >
                        Contact
                    </Link>
                    <Link
                        to="/free-trial"
                        data-testid="nav-book-trial-button"
                        className="rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-teal warm-shadow"
                    >
                        Book a Free Trial
                    </Link>
                </nav>
                <button
                    className="grid h-11 w-11 place-items-center rounded-full border border-teal/25 bg-white lg:hidden"
                    data-testid="nav-mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="h-5 w-5 text-teal" /> : <Menu className="h-5 w-5 text-teal" />}
                </button>
            </div>
            {open && (
                <nav className="border-t border-saffron/25 bg-white px-6 py-6 lg:hidden" data-testid="nav-links-mobile">
                    <div className="flex flex-col gap-4">
                        {LINKS.map((l) => (
                            <button
                                key={l.hash}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                onClick={() => goTo(l.hash)}
                                className="text-left text-base font-medium text-ink transition-colors hover:text-saffron"
                            >
                                {l.label}
                            </button>
                        ))}
                        <Link to="/contact" data-testid="nav-mobile-link-contact" onClick={() => setOpen(false)} className="text-base font-medium text-ink transition-colors hover:text-saffron">
                            Contact
                        </Link>
                        <Link
                            to="/free-trial"
                            data-testid="nav-mobile-book-trial-button"
                            onClick={() => setOpen(false)}
                            className="mt-2 rounded-full bg-saffron px-6 py-3 text-center text-sm font-medium text-cream"
                        >
                            Book a Free Trial
                        </Link>
                    </div>
                </nav>
            )}
            </div>
        </header>
    );
};
